import { useState, useCallback } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import api from 'services/api'
import useToast from 'hooks/useToast'
import { useAuth } from 'store/selectors'

dayjs.extend(relativeTime)

// One report (audio-streaming step 6b, step-6-social.md "Reports"): what was
// reported and what was seen — the quote's or the comment's snapshot, or the
// legacy app's free text about a person — who reported whom, how many people
// have blocked the reported account, and for a quote, or a comment's quote,
// where it stands now, with its whole cut to hear. An open report carries its actions; a closed one says who closed it
// and when. The list and a report's own page (the one a Slack post links to)
// both render it.

// The API's `target_kind`, in the page's words. A kind this page has not heard
// of renders as its key, so a later step's reports show before it is named.
const KINDS = {
  moment: {
    label: 'quote',
    help: 'A friend’s released quote, reported from the friends lane.',
  },
  user: {
    label: 'person',
    help: 'A person: the legacy app’s report with its free text, or the merged app’s with a reason.',
  },
  comment: {
    label: 'comment',
    help: 'A comment in a friend’s quote’s thread (step 6c), reported from the thread.',
  },
}

const STATUSES = {
  actioned: { label: 'actioned', color: 'success' },
  dismissed: { label: 'dismissed', color: 'default' },
}

// The 409s, by detail. Replaying the same action is a 200 with the row
// unchanged, never one of these.
const CONFLICTS = {
  not_open: 'Already closed another way.',
  not_a_moment_report: 'Not a quote report: there is no quote to disable.',
  not_a_comment_report:
    'Not a comment report: there is no comment to take back.',
  comment_gone:
    'The comment is gone: its thread went with its quote’s deleted account. Dismiss the report, or delete the author’s account.',
  no_reported_account: 'No reported account on this report: nobody to delete.',
}

const fmtAt = (ms) => dayjs(ms).format('ddd MMM D, YYYY HH:mm:ss')

// Where the reported quote stands now. The snapshot is what was seen; this is
// what a friend would see today.
const momentState = (moment) => {
  if (!moment || moment.deleted)
    return {
      label: 'deleted',
      color: 'default',
      help: 'The quote is gone: its owner deleted it. The snapshot is what was reported.',
    }
  if (moment.disabled_reason === 'admin_disabled')
    return {
      label: 'disabled by admin',
      color: 'error',
      help: `Hidden for everybody ${fmtAt(
        moment.disabled_at_ms
      )}, on a report. Its owner cannot restore it.`,
    }
  if (moment.disabled_at_ms != null)
    return {
      label: 'hidden by owner',
      color: 'default',
      help: `Its owner hid it ${fmtAt(moment.disabled_at_ms)} (${
        moment.disabled_reason || 'reason masked'
      }), and may restore it. Disabling it here keeps it hidden for good.`,
    }
  if (moment.released_at_ms != null)
    return {
      label: 'released',
      color: 'success',
      variant: 'outlined',
      help: `Released ${fmtAt(
        moment.released_at_ms
      )}: in its owner’s feed and readable by their friends.`,
    }
  return {
    label: 'not released',
    color: 'info',
    help: 'Waiting in its owner’s tray: no friend can read it.',
  }
}

// Where the reported comment stands now. The snapshot keeps its words either
// way; a comment taken back is blank in every thread.
export const TAKEN_BACK = {
  author: 'its author',
  owner: 'the quote’s owner',
  admin: 'an admin, on a report',
  account: 'its author’s account deletion',
}

const commentState = (comment) => {
  if (!comment)
    return {
      label: 'gone',
      color: 'default',
      help: 'The thread went with its quote’s deleted account. The snapshot is what was reported.',
    }
  if (comment.deleted_at_ms != null)
    return {
      label:
        comment.deleted_reason === 'admin'
          ? 'taken back by admin'
          : 'taken back',
      color: comment.deleted_reason === 'admin' ? 'error' : 'default',
      help: `Taken back ${fmtAt(comment.deleted_at_ms)} by ${
        TAKEN_BACK[comment.deleted_reason] || comment.deleted_reason
      }: blank in the thread for everybody.`,
    }
  return {
    label: 'in the thread',
    color: 'success',
    variant: 'outlined',
    help: `Said ${fmtAt(
      comment.created_at_ms
    )}; everybody who may read the quote reads it, but its reporter.`,
  }
}

// Relative in the text, the instant on hover.
const At = ({ ms }) =>
  ms == null ? (
    '—'
  ) : (
    <Tooltip title={fmtAt(ms)}>
      <Box component="span" sx={{ cursor: 'default' }}>
        {dayjs(ms).fromNow()}
      </Box>
    </Tooltip>
  )

const Person = ({ role, person, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: 0.75,
      rowGap: 0.25,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ width: 64 }}>
      {role}
    </Typography>
    {person ? (
      <>
        <Typography variant="body2" fontWeight={600}>
          {person.display_name || 'no name'}
        </Typography>
        {person.username && (
          <Typography variant="body2" color="text.secondary">
            @{person.username}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: 'monospace' }}
        >
          {person.id}
        </Typography>
        {person.deleted && <Chip size="small" label="deleted account" />}
      </>
    ) : (
      <Typography variant="body2" color="text.secondary">
        not on the report
      </Typography>
    )}
    {children}
  </Box>
)

// Each action's `help` is what a hover on its button says it does, in plain
// words; the confirm dialog says the rest.
const ACTIONS = {
  disable_moment: {
    label: 'Disable quote',
    help: 'Hide this quote from its owner’s profile and all their friends’ feeds, for good. The owner can’t bring it back. Closes the report.',
    call: (args) => api.reports.disableMoment(args),
    done: 'Quote disabled for everybody; report actioned.',
    confirm: (report) => ({
      title: 'Disable this quote?',
      body: `“${
        report.snapshot || 'the quote'
      }” is hidden for everybody: its owner’s feed and every friend’s. Its owner cannot restore it, and this page has no undo. The report is then actioned.`,
      button: 'Disable quote',
      color: 'warning',
    }),
  },
  tombstone_comment: {
    label: 'Take back comment',
    help: 'Immediately hide this comment for everyone, as if its author had deleted it. Closes the report.',
    call: (args) => api.reports.tombstoneComment(args),
    done: 'Comment taken back for everybody; report actioned.',
    confirm: (report) => ({
      title: 'Take this comment back?',
      body: `“${
        report.snapshot || 'the comment'
      }” is blanked in the thread for everybody, as if its author had taken it back, and this page has no undo. The report keeps its words, and is then actioned.`,
      button: 'Take back comment',
      color: 'warning',
    }),
  },
  dismiss: {
    label: 'Dismiss',
    help: 'Close the report without changing anything: whatever was reported stays as it is.',
    call: (args) => api.reports.dismiss(args),
    done: 'Report dismissed.',
    confirm: null,
  },
  disable_account: {
    label: 'Delete account',
    help: 'Delete this person’s account and everything in it. This can’t be undone.',
    call: (args) => api.reports.disableAccount(args),
    done: 'Account deleted, and every report about it with it.',
    confirm: (report) => ({
      title: 'Delete this account? This cannot be undone.',
      body: `${report.reported?.display_name || 'no name'}${
        report.reported?.username ? ` (@${report.reported.username})` : ''
      }, ${
        report.reported?.id
      }. The account is deleted in full, the same deletion as deleting the user anywhere else in admin, and every report about it goes with it, this one included.`,
      button: 'Delete account',
      color: 'error',
    }),
  },
}

// The actions for a page: the confirm dialog, which report has one in flight,
// the toast. `onSettled(report, action, row)` hears the answer row; on a
// `not_open` conflict it hears the report as it now stands, re-read, with no
// action. Both pages render `dialog`.
export const useReportActions = (onSettled) => {
  const toast = useToast()
  // { report, action } waiting on the dialog.
  const [pending, setPending] = useState(null)
  // Report id → the action in flight on it.
  const [busy, setBusy] = useState({})

  const run = useCallback(
    async (report, action) => {
      setPending(null)
      setBusy((current) => ({ ...current, [report.id]: action }))
      try {
        const row = await ACTIONS[action].call({ reportId: report.id })
        toast({ message: ACTIONS[action].done, severity: 'success' })
        onSettled(report, action, row)
      } catch (err) {
        const conflict = CONFLICTS[err.message]
        toast({
          message: conflict ? `${conflict} (${err.message})` : err.message,
          severity: conflict ? 'warning' : 'error',
        })
        if (err.message === 'not_open') {
          try {
            const row = await api.reports.getReport({ reportId: report.id })
            onSettled(report, null, row)
          } catch {
            // the toast has said what happened
          }
        }
      } finally {
        setBusy((current) => {
          const next = { ...current }
          delete next[report.id]
          return next
        })
      }
    },
    [toast, onSettled]
  )

  const request = useCallback(
    (report, action) =>
      ACTIONS[action].confirm
        ? setPending({ report, action })
        : run(report, action),
    [run]
  )

  const confirm = pending && ACTIONS[pending.action].confirm(pending.report)
  const dialog = (
    <Dialog open={Boolean(pending)} onClose={() => setPending(null)}>
      <DialogTitle>{confirm?.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{confirm?.body}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPending(null)}>Cancel</Button>
        <Button
          variant="contained"
          color={confirm?.color}
          onClick={() => run(pending.report, pending.action)}
        >
          {confirm?.button}
        </Button>
      </DialogActions>
    </Dialog>
  )

  return { request, busy, dialog }
}

// The page's one audio element, for `usePlayer` (the Moments page's).
export const PlayerAudio = ({ player }) => (
  <audio
    ref={player.audioRef}
    onTimeUpdate={player.onTimeUpdate}
    onPlay={() => player.setPaused(false)}
    onPause={() => player.setPaused(true)}
    onEnded={() => player.setPaused(true)}
    style={{ display: 'none' }}
  />
)

// A click on a button or the player inside a card that opens on click.
const own = (event) => {
  event.stopPropagation()
}

// The quote's whole cut, through the Moments page's route (`moment.audio`).
// In the card's gutter on a quote report, where the quote is what was
// reported; inline in the "Its quote" row on a comment report, where it is the
// context the comment was said under.
const QuotePlay = ({ moment, player, inline, help }) => {
  const playing = moment && player.current?.id === moment.id
  const wholePlaying = playing && player.current.whole && !player.paused
  return (
    <Tooltip
      title={
        moment?.audio
          ? wholePlaying
            ? 'Pause'
            : help
          : 'No audio: the quote is deleted or its cut failed'
      }
    >
      <span onClick={own}>
        <IconButton
          size="small"
          sx={inline ? { p: 0.25 } : {}}
          disabled={!moment?.audio}
          onClick={() => player.playWhole({ id: moment.id })}
        >
          {player.loading === moment?.id ? (
            <CircularProgress size={inline ? 14 : 18} />
          ) : wholePlaying ? (
            <PauseIcon fontSize={inline ? 'small' : 'medium'} />
          ) : (
            <PlayArrowIcon fontSize={inline ? 'small' : 'medium'} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  )
}

// `showOpen` marks an open report too, where open ones sit beside closed ones
// (a person's profile); the Reports page's open queue needs no mark.
const ReportCard = ({ report, player, onOpen, onAct, busy, showOpen }) => {
  const { user } = useAuth()
  const kind = KINDS[report.target_kind]
  const status = STATUSES[report.status]
  const moment = report.moment
  const hasQuote = Boolean(moment) || report.target_kind === 'moment'
  const state = hasQuote ? momentState(moment) : null
  const isComment = report.target_kind === 'comment'
  const said = isComment ? commentState(report.comment) : null
  const reported = report.reported
  const open = report.status === 'open'

  const openCard = () => {
    if (!onOpen) return
    // Selecting text (an id to copy) ends in a click; it is not a request to
    // open the report.
    if (window.getSelection()?.toString()) return
    onOpen(report)
  }

  return (
    <Paper
      variant="outlined"
      onClick={openCard}
      sx={{
        p: 2,
        borderRadius: 2,
        cursor: onOpen ? 'pointer' : 'default',
        '&:hover': onOpen ? { borderColor: 'primary.main' } : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {hasQuote && !isComment && (
          <Box sx={{ mt: 0.25 }}>
            <QuotePlay
              moment={moment}
              player={player}
              help="Play the whole cut"
            />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Tooltip title={kind?.help || ''}>
              <Chip
                size="small"
                variant="outlined"
                label={kind?.label || report.target_kind}
              />
            </Tooltip>
            {report.status == null && (
              <Tooltip title="Written before step 6, with no status: handled in Slack, and never on a list.">
                <Chip size="small" label="no status" />
              </Tooltip>
            )}
            {status && (
              <Chip size="small" color={status.color} label={status.label} />
            )}
            {showOpen && report.status === 'open' && (
              <Chip size="small" color="warning" label="open" />
            )}
            <Typography variant="caption" color="text.secondary">
              reported <At ms={report.created_at_ms} />
              {report.reason ? ` · reason: ${report.reason}` : ''}
            </Typography>
          </Box>
          {report.snapshot && isComment ? (
            // A comment is somebody's words in a thread, not a quote of the
            // recording: set off with a bar, as a chat app shows a reply's
            // context, rather than in quotation marks.
            <Typography
              variant="body1"
              sx={{
                borderLeft: 3,
                borderColor: 'text.disabled',
                pl: 1.5,
                py: 0.25,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
              }}
            >
              {report.snapshot}
            </Typography>
          ) : report.snapshot ? (
            <Typography
              variant={report.target_kind === 'moment' ? 'h6' : 'body1'}
              fontWeight={700}
              sx={{ lineHeight: 1.45 }}
            >
              “{report.snapshot}”
            </Typography>
          ) : null}
          {report.description ? (
            <Box sx={{ mt: report.snapshot ? 1 : 0 }}>
              <Typography variant="caption" color="text.secondary">
                The reporter wrote
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {report.description}
              </Typography>
            </Box>
          ) : null}
          {!report.snapshot && !report.description && (
            <Typography variant="body2" color="text.secondary">
              {report.target_kind === 'user'
                ? 'The person themself: no text came with the report.'
                : 'No snapshot on this report.'}
            </Typography>
          )}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5 }}
          >
            <Person role="Reported" person={reported}>
              {reported && (
                <Tooltip title="How many people have blocked this account: block rows that exist, from anyone, whatever the reason.">
                  <Chip
                    size="small"
                    variant="outlined"
                    color={
                      report.reported_blocks_received > 0
                        ? 'warning'
                        : 'default'
                    }
                    label={`blocked by ${report.reported_blocks_received}`}
                  />
                </Tooltip>
              )}
            </Person>
            <Person role="Reporter" person={report.reporter} />
            {isComment && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  columnGap: 0.75,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 64 }}
                >
                  Comment
                </Typography>
                <Tooltip title={said.help}>
                  <Chip
                    size="small"
                    color={said.color}
                    variant={said.variant || 'filled'}
                    label={said.label}
                  />
                </Tooltip>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace' }}
                >
                  {report.target_id}
                </Typography>
              </Box>
            )}
            {hasQuote && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  columnGap: 0.75,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 64 }}
                >
                  {report.target_kind === 'moment' ? 'Quote' : 'Its quote'}
                </Typography>
                {isComment && (
                  <QuotePlay
                    moment={moment}
                    player={player}
                    inline
                    help="Play the quote this comment was said under"
                  />
                )}
                <Tooltip title={state.help}>
                  <Chip
                    size="small"
                    color={state.color}
                    variant={state.variant || 'filled'}
                    label={state.label}
                  />
                </Tooltip>
                {moment && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {moment.id}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {!open && report.actioned_at_ms != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1.5 }}
            >
              {status?.label || report.status} by{' '}
              {report.actioned_by && report.actioned_by === user?.id ? (
                'you'
              ) : (
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  {report.actioned_by || 'unknown'}
                </Box>
              )}{' '}
              · <At ms={report.actioned_at_ms} />
            </Typography>
          )}
          {moment && player.error?.id === moment.id && (
            <Alert severity="error" sx={{ mt: 1 }} onClick={own}>
              {player.error.message}
            </Alert>
          )}
        </Box>
        {open && onAct && (
          <Box
            onClick={own}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              alignItems: 'stretch',
              minWidth: 150,
            }}
          >
            {busy ? (
              <CircularProgress size={20} sx={{ alignSelf: 'center' }} />
            ) : (
              <>
                {report.target_kind === 'moment' && (
                  <Tooltip title={ACTIONS.disable_moment.help} placement="left">
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => onAct(report, 'disable_moment')}
                    >
                      {ACTIONS.disable_moment.label}
                    </Button>
                  </Tooltip>
                )}
                {isComment && (
                  <Tooltip
                    title={
                      !report.comment
                        ? 'The comment is gone with its quote: dismiss the report, or delete the author’s account.'
                        : report.comment.deleted_at_ms != null
                        ? 'Already blank in the thread: this closes the report as actioned.'
                        : ACTIONS.tombstone_comment.help
                    }
                    placement="left"
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        fullWidth
                        disabled={!report.comment}
                        onClick={() => onAct(report, 'tombstone_comment')}
                      >
                        {ACTIONS.tombstone_comment.label}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={ACTIONS.dismiss.help} placement="left">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onAct(report, 'dismiss')}
                  >
                    {ACTIONS.dismiss.label}
                  </Button>
                </Tooltip>
                {reported && (
                  <Tooltip
                    title={
                      reported.deleted
                        ? 'The account is already deleted: dismiss the report instead.'
                        : ACTIONS.disable_account.help
                    }
                    placement="left"
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        fullWidth
                        disabled={reported.deleted}
                        onClick={() => onAct(report, 'disable_account')}
                      >
                        {ACTIONS.disable_account.label}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  )
}

export default ReportCard
